import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { fetchCapabilities } from "../api/capabilities";
import { useEditorStore } from "./store/editorStore";
import { Canvas } from "./canvas/Canvas";
import { InspectorPanel } from "./inspector/InspectorPanel";
import { PreviewPane } from "./PreviewPane";
import { ValidationPanel } from "./ValidationPanel";

export function EditorShell() {
    const document_ = useEditorStore((s) => s.document);
    const saveState = useEditorStore((s) => s.saveState);
    const saveError = useEditorStore((s) => s.saveError);
    const conflict = useEditorStore((s) => s.conflict);
    const validation = useEditorStore((s) => s.validation);
    const save = useEditorStore((s) => s.save);
    const undo = useEditorStore((s) => s.undo);
    const redo = useEditorStore((s) => s.redo);
    const undoDepth = useEditorStore((s) => s.undoStack.length);
    const redoDepth = useEditorStore((s) => s.redoStack.length);
    const applyDocChange = useEditorStore((s) => s.applyDocChange);
    const selectedId = useEditorStore((s) => s.selectedId);
    const select = useEditorStore((s) => s.select);
    const [showPreview, setShowPreview] = useState(true);
    const [showChecks, setShowChecks] = useState(false);
    const [edenEnabled, setEdenEnabled] = useState(false);
    const [edenStatus, setEdenStatus] = useState<string | null>(null);
    const emailId = useEditorStore((s) => s.emailId);

    useEffect(() => {
        void fetchCapabilities().then((c) => setEdenEnabled(c.eden));
    }, []);

    const syncToEden = async () => {
        if (!emailId) return;
        setEdenStatus("Syncing…");
        try {
            const result = await api.post<{ action: string }>(`/api/v1/eden/sync/${emailId}`);
            setEdenStatus(result.action === "unchanged" ? "Eden: up to date" : "Eden: synced");
        } catch (e) {
            setEdenStatus(`Eden: failed (${e instanceof Error ? e.message : e})`);
        }
    };

    const onKeyDown = useCallback(
        (e: KeyboardEvent) => {
            const inEditable =
                (e.target as HTMLElement)?.closest("input, textarea, [contenteditable=true]") !=
                null;
            const mod = e.ctrlKey || e.metaKey;
            if (mod && e.key.toLowerCase() === "s") {
                e.preventDefault();
                void save();
            } else if (mod && !inEditable && e.key.toLowerCase() === "z" && !e.shiftKey) {
                e.preventDefault();
                undo();
            } else if (
                mod &&
                !inEditable &&
                (e.key.toLowerCase() === "y" || (e.key.toLowerCase() === "z" && e.shiftKey))
            ) {
                e.preventDefault();
                redo();
            } else if (!inEditable && e.key === "Delete" && selectedId) {
                e.preventDefault();
                applyDocChange((d) => {
                    const remove = (parent: typeof d.root): boolean => {
                        const idx = parent.children?.findIndex((c) => c.id === selectedId) ?? -1;
                        if (idx >= 0) {
                            if (parent.children![idx]!.locked) return true; // locked: no delete
                            parent.children!.splice(idx, 1);
                            return true;
                        }
                        return (parent.children ?? []).some(remove);
                    };
                    remove(d.root);
                });
                select(null);
            }
        },
        [save, undo, redo, selectedId, applyDocChange, select],
    );

    useEffect(() => {
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [onKeyDown]);

    if (!document_) return null;

    const errorCount = validation.filter((w) => w.severity === "error").length;
    const warnCount = validation.filter((w) => w.severity === "warning").length;

    return (
        <div className="editor-shell">
            <header className="topbar editor-topbar">
                <a
                    href={
                        (typeof window !== "undefined" &&
                            (window as unknown as { __EMAIL_EDITOR__?: { backUrl?: string } }).__EMAIL_EDITOR__
                                ?.backUrl) ||
                        "/outbound/templates"
                    }
                    className="topbar-back"
                    aria-label="Back"
                >
                    ←
                </a>
                <input
                    className="title-input"
                    value={document_.meta.title}
                    aria-label="Email title"
                    onChange={(e) =>
                        applyDocChange(
                            (d) => {
                                d.meta.title = e.target.value;
                            },
                            { coalesceKey: "title" },
                        )
                    }
                />
                <div className="topbar-actions">
                    <button type="button" className="tb-btn" onClick={undo} disabled={undoDepth === 0} title="Undo (Ctrl+Z)" data-testid="undo">
                        ↩
                    </button>
                    <button type="button" className="tb-btn" onClick={redo} disabled={redoDepth === 0} title="Redo (Ctrl+Y)" data-testid="redo">
                        ↪
                    </button>
                    <button
                        type="button"
                        className={`validate-badge${errorCount > 0 ? " errors" : warnCount > 0 ? " warnings" : ""}`}
                        onClick={() => setShowChecks((v) => !v)}
                        aria-pressed={showChecks}
                        data-testid="validate-badge"
                    >
                        {errorCount > 0 ? `${errorCount} errors` : warnCount > 0 ? `${warnCount} warnings` : "OK"}
                    </button>
                    <button
                        type="button"
                        className="tb-btn"
                        onClick={async () => {
                            const name = window.prompt("Save as template — name:", document_.meta.title);
                            if (!name) return;
                            const { api } = await import("../api/client");
                            await api.post("/api/v1/templates", {
                                name,
                                scope: "tenant",
                                document: document_,
                            });
                            window.alert(`Template "${name}" saved`);
                        }}
                        data-testid="save-as-template"
                    >
                        Save as template
                    </button>
                    <button
                        type="button"
                        className="tb-btn"
                        onClick={() => setShowPreview((v) => !v)}
                        aria-pressed={showPreview}
                    >
                        Preview
                    </button>
                    {edenEnabled && (
                        <button
                            type="button"
                            className="tb-btn"
                            onClick={() => void syncToEden()}
                            title={edenStatus ?? "Push this email's record to The Eden Engine"}
                            data-testid="eden-sync"
                        >
                            {edenStatus ?? "Sync to Eden"}
                        </button>
                    )}
                    <span
                        className={`save-state ${saveState}`}
                        data-testid="save-state"
                        role="status"
                        aria-live="polite"
                    >
                        {saveState === "saving"
                            ? "Saving…"
                            : saveState === "saved"
                              ? "Saved"
                              : saveState === "dirty"
                                ? "Unsaved changes"
                                : saveState === "conflict"
                                  ? "Conflict!"
                                  : saveState === "error"
                                    ? `Save failed: ${saveError}`
                                    : ""}
                    </span>
                    <button type="button" className="tb-btn primary" onClick={() => void save()} data-testid="save">
                        Save
                    </button>
                </div>
            </header>

            <div className="editor-main">
                <Canvas document={document_} />
                <InspectorPanel document={document_} />
                {showPreview && <PreviewPane document={document_} />}
            </div>

            {showChecks && <ValidationPanel onClose={() => setShowChecks(false)} />}
            {conflict && <ConflictDialog />}
        </div>
    );
}

function ConflictDialog() {
    const conflict = useEditorStore((s) => s.conflict);
    const reload = useEditorStore((s) => s.resolveConflictReload);
    const overwrite = useEditorStore((s) => s.resolveConflictOverwrite);
    if (!conflict) return null;
    return (
        <div className="edm-modal-backdrop" role="dialog" aria-modal="true" aria-label="Save conflict" data-testid="conflict-dialog">
            <div className="edm-modal">
                <h3>Someone else saved this email</h3>
                <p>
                    <strong>{conflict.updatedBy}</strong> saved version {conflict.currentVersion}{" "}
                    while you were editing. Choose how to continue:
                </p>
                <div className="modal-actions">
                    <button type="button" className="tb-btn" onClick={reload} data-testid="conflict-reload">
                        Load their version (discard mine)
                    </button>
                    <button type="button" className="tb-btn primary" onClick={() => void overwrite()} data-testid="conflict-overwrite">
                        Keep mine (overwrite theirs)
                    </button>
                </div>
            </div>
        </div>
    );
}
